import type { ToolDefinition, ToolType } from "../types";

const IMAGE_DPI_CHANGER_TOOL_TYPE = {
  label: "DPI Tool — Check & Change",
  icon: "Ruler",
} as const satisfies ToolType;

const imageDpiChangerTool: ToolDefinition = {
  slug: "image-dpi-changer",
  name: "Change Image DPI Online | Free 300 DPI Converter & Checker",
  title: "Image DPI Changer",
  description:
    "Change image DPI online instantly. Convert photos to 300 DPI for professional printing, check current resolution, and adjust print density without losing quality.",
  longDescription:
    "Easily change the DPI (Dots Per Inch) of your images for high-quality printing. Whether you need to convert a JPG to 300 DPI for a professional portfolio or 600 DPI for archival scanning, our free online tool can update metadata only or resample pixels when your workflow requires physical print size preservation.",
  toolType: {
    label: "DPI Converter",
    icon: "Printer",
  },
  heroBadges: [
    "100% Free",
    "No Account Required",
    "Custom DPI Input",
    "Built-in DPI Checker",
    "Metadata or Resample Mode",
  ],
  category: "Editing Tools",
  categorySlug: "image-editing-tools",
  href: "/tools/image-dpi-changer",
  api: "/api/v1/image-dpi-changer",
  keywords: [
    "snappy image dpi converter app",
    "convert jpg to 300 dpi",
    "check jpeg print ppi online",
    "600 dpi image print inspector",
    "adjust picture dimensions online",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Change Image DPI Online",
        description:
          "Follow these quick steps to check your current image resolution and convert it to a print-ready format.",
      },
      benefits: {
        title: "Why Use Our Image DPI Resizer?",
        description:
          "Ensure your digital artwork and photography print sharply by managing exact metadata specifications.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Learn the difference between DPI and pixels, how to prepare files for print, and how our converter works.",
      },
    },
    introBlocks: [
      {
        kind: "text",
        title: "Change Picture DPI Online Instantly",
        level: "h2",
        paragraphs: [
          "Whether you are preparing family photos for a physical album, submitting artwork to a gallery, or ensuring your digital documents meet legal print standards, getting the correct print resolution is crucial. Our image DPI converter allows you to quickly adjust image DPI metadata to exact professional standards without needing to purchase or install heavy desktop software.",
          "Many users search for ways to increase picture resolution online or improve photo quality online free, but it is important to understand what a DPI converter actually does. DPI (Dots Per Inch) tells a physical printer how densely to pack pixels on a piece of paper. Metadata-only mode changes that embedded print instruction without altering the pixels. Resample mode is available when you need the pixel dimensions recalculated to preserve the original physical print size at the new DPI.",
        ],
        note: "For the safest result, use Metadata only. Choose Resample pixels only when you intentionally want the image dimensions resized for a target print size.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Common Print Resolution Standards",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "72 to 96 DPI (Web Standard)",
                icon: "Monitor",
                description:
                  "Optimized for screens and digital viewing. Not recommended for physical printing.",
                accent: "primary",
              },
              {
                label: "150 DPI (Drafts & Posters)",
                icon: "FileText",
                description:
                  "Suitable for large format banners viewed from a distance or quick document drafts.",
                accent: "primary",
              },
              {
                label: "300 DPI (Professional Print)",
                icon: "Printer",
                description:
                  "The golden standard for magazines, photographs, flyers, and professional documents.",
                accent: "primary",
              },
              {
                label: "600 DPI (High-End Archival)",
                icon: "Camera",
                description:
                  "Used for fine art prints, high-quality scanning, and detailed line art.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "text",
        title: "Built-In Image DPI Checker",
        level: "h2",
        paragraphs: [
          "Before you use our 300 dpi converter, you might want to know the current state of your file. Our tool includes a robust built-in DPI checker that allows you to inspect the embedded metadata of your JPEG, PNG, WebP, or HEIC files instantly. By clicking the purple 'Check DPI' button, you can immediately see the current print density and exact pixel dimensions of your graphic.",
          "If your image is already set to the correct print density, you save time. If it is set to a standard web resolution, you can seamlessly switch modes, enter your custom target value, and convert photos to 300 DPI with a single click. The tool automatically renames your downloaded file to include the new DPI tag (e.g., photo_300dpi.jpg) so your print shop knows it is ready.",
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "How DPI Affects Physical Print Size",
            description:
              "See how the same image prints at different physical sizes depending on its DPI setting.",
            columns: [
              "Original Pixel Dimensions",
              "Print Size at 72 DPI",
              "Print Size at 300 DPI",
            ],
            rows: [
              {
                "Original Pixel Dimensions": "1200 x 1800 px",
                "Print Size at 72 DPI": "16.6 x 25.0 inches (Blurry)",
                "Print Size at 300 DPI": "4 x 6 inches (Sharp)",
              },
              {
                "Original Pixel Dimensions": "2400 x 3000 px",
                "Print Size at 72 DPI": "33.3 x 41.6 inches (Blurry)",
                "Print Size at 300 DPI": "8 x 10 inches (Sharp)",
              },
              {
                "Original Pixel Dimensions": "3000 x 4500 px",
                "Print Size at 72 DPI": "41.6 x 62.5 inches (Blurry)",
                "Print Size at 300 DPI": "10 x 15 inches (Sharp)",
              },
            ],
            note: "To calculate maximum sharp print size, divide your pixel width and height by 300.",
            emphasisColumns: ["Original Pixel Dimensions"],
            monospaceColumns: [],
          },
        ],
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload your image",
        description:
          "Upload your JPEG, PNG, WebP, or HEIC image using the drag-and-drop zone or click to browse. A preview loads immediately to confirm the correct file.",
      },
      {
        number: "02",
        title: "Check your current DPI (optional)",
        description:
          "Click the purple 'Check DPI' button to see your image's current DPI, pixel dimensions, and format before changing anything. This helps confirm whether a DPI change is needed.",
      },
      {
        number: "03",
        title: "Select Change DPI, target, and mode",
        description:
          "Click the pink 'Change DPI' button to switch to changer mode. Enter your target DPI, then keep Metadata only for a fast DPI tag update or choose Resample pixels to resize dimensions while preserving physical print size.",
      },
      {
        number: "04",
        title: "Download your print-ready image",
        description:
          "Click the final Change DPI button to process. Download the updated image — the filename automatically includes the new DPI value so it is easily identifiable for your print workflow.",
      },
    ],
    benefits: [
      {
        icon: "Edit3",
        title: "Any Custom Value",
        description:
          "Don't be limited by presets. Change image to 300 DPI, convert to 600 DPI, or set any custom whole number you need for your specific project.",
      },
      {
        icon: "ShieldCheck",
        title: "Safe Metadata Mode",
        description:
          "The default mode only updates the internal print metadata for normal-sized images, helping preserve the original pixel data.",
      },
      {
        icon: "Maximize",
        title: "Optional Resampling",
        description:
          "When a print workflow needs resized pixel dimensions, resample mode recalculates dimensions to preserve physical print size at the target DPI.",
      },
      {
        icon: "Search",
        title: "Integrated Inspector",
        description:
          "Stop guessing your file's resolution. Check DPI of image online instantly with our integrated reading tool before applying changes.",
      },
      {
        icon: "Tag",
        title: "Smart File Renaming",
        description:
          "Downloaded files are automatically appended with their new DPI value, keeping your pre-press folders perfectly organized.",
      },
    ],
    faqs: [
      {
        question:
          "Is it possible to convert a 72 DPI image to 300 DPI for high-quality printing?",
        answer:
          "Yes, absolutely. By uploading a 72 DPI image to our tool and entering 300 as the target, we rewrite the file's metadata. However, remember that changing the DPI does not add new pixels. If your original image is only 500x500 pixels, it will print sharply at 300 DPI, but the physical printed size will be very small (about 1.6 x 1.6 inches).",
      },
      {
        question:
          "What is the difference between PPI and DPI for online photo editing?",
        answer:
          "PPI (Pixels Per Inch) refers to digital screens and the density of pixels on a monitor. DPI (Dots Per Inch) is a printing term referring to the physical ink dots a printer places on a piece of paper. In digital file metadata, the terms are often used interchangeably to represent the 'print resolution' tag embedded in the image.",
      },
      {
        question:
          "How can I increase image resolution without losing quality using AI in 2026?",
        answer:
          "If you need to increase the actual pixel dimensions of a tiny, blurry image, you need an AI image upscaler, not a DPI changer. AI upscalers guess and generate new pixels to make the image larger. Once the image has enough pixels, you can then use our DPI tool to tag it as 300 DPI for printing.",
      },
      {
        question:
          "Why do my upscaled photos look plastic or blurry after using online tools?",
        answer:
          "AI upscalers often 'hallucinate' details, which can make textures look like smooth plastic or oil paintings. This is why, if your camera already captured a high-megapixel photo, it is much better to simply change the DPI metadata using our tool rather than running it through an AI resizer that might ruin the natural textures.",
      },
      {
        question:
          "How do I change the DPI of a photo on an iPhone without third-party apps?",
        answer:
          "iPhones do not have a built-in feature in the native Photos app to change DPI metadata. The easiest method is to open your mobile Safari or Chrome browser, navigate to our free online DPI changer, upload your photo from your camera roll, set it to 300, and download it right back to your phone.",
      },
      {
        question: "Does changing the DPI increase the file size?",
        answer:
          "Metadata-only changes usually keep the file size close to the original because the pixel dimensions are not changed. Resample mode can increase or decrease file size because it intentionally resizes the image pixels before writing the DPI metadata.",
      },
      {
        question: "When should I use resample mode?",
        answer:
          "Use resample mode only when you need the image's physical print size to stay the same after changing DPI. If your image has no embedded source DPI, the service assumes 72 DPI for the calculation and limits fallback targets to 300 DPI.",
      },
      {
        question: "Can I convert JPG to 600 DPI for archival scanning?",
        answer:
          "Yes. 600 DPI is the standard for high-end archival storage and fine art reproduction. Simply upload your high-resolution JPG or TIFF, enter 600 in the Target DPI box, and generate your updated file.",
      },
    ],
    useCases: [
      {
        label: "Print Preparation",
        icon: "Printer",
        description:
          "Convert JPG to 300 DPI to ensure your marketing flyers, business cards, and brochures pass commercial printer quality checks.",
        accent: "primary",
      },
      {
        label: "Artwork Submission",
        icon: "Brush",
        description:
          "Easily meet gallery and art competition requirements by tagging your digital portfolios with exact 300 or 600 DPI metadata.",
        accent: "primary",
      },
      {
        label: "Legal Documentation",
        icon: "Scale",
        description:
          "Prepare scanned legal documents and ID cards to exact resolution standards required by government and visa web portals.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Graphic Designers",
        icon: "PenTool",
        description:
          "Quickly fix web-resolution assets sent by clients before placing them into print-ready InDesign or Illustrator layouts.",
        accent: "accent",
      },
      {
        label: "Photographers",
        icon: "Camera",
        description:
          "Organize client delivery folders by properly tagging high-res exports for print and low-res exports for web viewing.",
        accent: "accent",
      },
      {
        label: "Everyday Users",
        icon: "User",
        description:
          "Fix 'resolution too low' errors when uploading family photos to canvas printing sites like CVS, Walgreens, or Shutterfly.",
        accent: "accent",
      },
    ],
  },
};

export default imageDpiChangerTool;
export { imageDpiChangerTool };
